import {Component, OnDestroy, OnInit} from '@angular/core';
import {Update} from '@ngrx/entity';
import {Observable, Subscription} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {SubheaderService} from '../../../../core/_base/layout';
import {AppState} from '../../../../core/reducers';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {select, Store} from '@ngrx/store';
import {delay} from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import {combineLatest} from 'rxjs'

import {
    MeasurementMethodology,
    MeasurementMethodologyOnServerCreated,
    selectMeasurementMethodologiesActionLoading,
    selectMeasurementMethodologyById,
    selectLastCreatedMeasurementMethodologyId, MeasurementMethodologyUpdated
} from '../../../../core/linked-tables';
import {Location} from '@angular/common';

@Component({
    selector: 'kt-measurement-methodology-edit',
    templateUrl: './measurement-methodology-edit.component.html',
    styleUrls: ['./measurement-methodology-edit.component.scss'],
})


export class MeasurementMethodologyEditComponent implements OnInit, OnDestroy {

    model: MeasurementMethodology;
    oldModel: MeasurementMethodology;

    measurementMethodologyForm: UntypedFormGroup;
    hasFormErrors = false;

    loading$: Observable<boolean>;
    viewLoading = false;
    loadingAfterSubmit = false;
    mode = 'NEW';
    // Private properties
    private componentSubscriptions: Subscription;
    private subscriptions: Subscription[] = [];

    readonly = false

    /**
     * Component constructor
     *
     * @param subheaderService: SubheaderService
     * @param activatedRoute: ActivatedRoute
     * @param router: Router
     * @param fb: FormBuilder
     * @param location: Location
     * @param store: Store<AppState>
     */
    constructor(public subheaderService: SubheaderService,
                private activatedRoute: ActivatedRoute,
                private router: Router,
                private fb: UntypedFormBuilder,
                private location: Location,
                private store: Store<AppState>,
                private translate: TranslateService) {
    }

    ngOnInit() {
        this.loading$ = this.store.pipe(select(selectMeasurementMethodologiesActionLoading));
        const routeSubscription = combineLatest([
            this.activatedRoute.params,
            this.activatedRoute.data
        ]).subscribe(([params, data]) => {
            const id = params.id;
            this.readonly = data.readonly
            if (id && id > 0) {
                this.store.pipe(select(selectMeasurementMethodologyById(id))).subscribe(res => {
                    if (res) {
                        this.oldModel = Object.assign({}, res);
                        this.model = Object.assign({}, this.oldModel);
                        this.initMeasurementMethodology();
                    }
                });
            } else {
                this.model = new MeasurementMethodology();
                this.model.clear();
                this.oldModel = Object.assign({}, this.model);
                this.initMeasurementMethodology();
            }
        });
        this.subscriptions.push(routeSubscription);

        if (this.activatedRoute.snapshot.paramMap.get('id') != null) {
            this.setBreadCrumbs(Number(this.activatedRoute.snapshot.paramMap.get('id')));
        } else {
            this.setBreadCrumbs(0);
        }
    }

    setBreadCrumbs(id: number): void {
        this.subheaderService.setTitle(this.getComponentTitle());
        this.subheaderService.setBreadcrumbs([
            {title: 'Developer', page: `../admin-management`},
            {title: 'Linked tables', page: `../admin-management/linked-tables`},
            {title: 'Measurement Methodologies', page: `../admin-management/linked-tables/measurement-methodologies`},
            {
                title: this.getComponentTitle(),
                page: id > 0 ? `../admin-management/linked-tables/measurement-methodologies/edit/${id}`
                    : `../admin-management/linked-tables/measurement-methodologies/add`
            }
        ]);
    }

    ngOnDestroy(): void {
        if (this.componentSubscriptions) {
            this.componentSubscriptions.unsubscribe();
        }
    }

    initMeasurementMethodology() {
        this.createForm();
    }


    createForm() {
        this.measurementMethodologyForm = this.fb.group({
            name: [this.model.name, Validators.required],
        });
    }

    /**
     * Returns page title
     */
    getComponentTitle(): string {
        if (this.model && this.model.id > 0) {
            this.mode = 'EDIT';
            return this.translate.instant('MEASUREMENT_METHODOLOGY.FORM.TITLE.EDIT', {name: this.model.name});
        }
        return this.translate.instant('MEASUREMENT_METHODOLOGY.FORM.TITLE.NEW');
    }

    /**
     * Check control is invalid
     * @param controlName: string
     */
    isControlInvalid(controlName: string): boolean {
        const control = this.measurementMethodologyForm.controls[controlName];
        return control.invalid && control.touched;
    }

    reset() {
        this.model = Object.assign({}, this.oldModel);
        this.createForm();
        this.hasFormErrors = false;
        this.measurementMethodologyForm.markAsPristine();
        this.measurementMethodologyForm.markAsUntouched();
        this.measurementMethodologyForm.updateValueAndValidity();
    }

    /** ACTIONS */

    /**
     * On Submit
     */
    onSubmit(withBack: boolean = false) {
        this.hasFormErrors = false;
        const controls = this.measurementMethodologyForm.controls;
        /** check form */
        if (this.measurementMethodologyForm.invalid) {
            Object.keys(controls).forEach(controlName =>
                controls[controlName].markAsTouched()
            );

            this.hasFormErrors = true;
            return;
        }

        if (this.model.id > 0) {
            this.updateMeasurementMethodology(this.model);
        } else {
            this.createMeasurementMethodology(this.model);
        }
    }

    /**
     * Update model
     *
     * @param _measurementMethodology: MeasurementMethodologyModel
     */
    updateMeasurementMethodology(_measurementMethodology: MeasurementMethodology) {
        this.loadingAfterSubmit = true;
        this.viewLoading = true;

        const updateMeasurementMethodology: Update<MeasurementMethodology> = {
            id: _measurementMethodology.id,
            changes: _measurementMethodology
        };
        this.store.dispatch(new MeasurementMethodologyUpdated({
            partialItem: updateMeasurementMethodology,
            item: _measurementMethodology
        }));

        const message = `Measurement Methodology successfully has been saved.`;
        this.navigateToParent();
    }

    /**
     * Create model
     *
     * @param _measurementMethodology: MeasurementMethodologyModel
     */
    createMeasurementMethodology(_measurementMethodology: MeasurementMethodology) {
        this.store.dispatch(new MeasurementMethodologyOnServerCreated({item: _measurementMethodology}));
        this.componentSubscriptions = this.store.pipe(
            select(selectLastCreatedMeasurementMethodologyId),
            delay(1000), // Remove this line
        ).subscribe(res => {
            if (!res) {
                return;
            }
            this.navigateToParent();
        });
    }

    /** Alect Close event */
    onAlertClose($event) {
        this.hasFormErrors = false;
    }


    isFormValid() {
        return (this.model && this.model.name && this.model.name.length > 0);
    }

    navigateToParent() {
        let url = '../';
        if (this.model.id > 0) {
            url = '../../';
        }
        this.router.navigate([url], {relativeTo: this.activatedRoute});
    }

    canDeactivate() {
        if (this.discard()) {
            if (window.confirm(this.translate.instant('GENERAL.MESSAGE.LOST_CHANGES'))) {
                return true;
            } else {
                // ---------work around angular bug--------- reference: https://github.com/angular/angular/issues/13586
                const currentUrlTree = this.router.createUrlTree([], this.activatedRoute.snapshot);
                const currentUrl = currentUrlTree.toString();
                this.location.go(currentUrl);
                // ---------work around end-----------------
                return false;
            }
        }
        return true;
    }

    discard() {
        if (this.model && this.oldModel) {
            return this.model.name != this.oldModel.name;
        }
        return false;
    }

}
